import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { applyUndoPayload, type UndoPayload } from '../api/channelBulk';
import { getApiErrorMessage } from '../api/http';
import { useToast } from './ToastContext';

const MAX_STACK = 20;

export type UndoRedoEntry = {
  label?: string;
  undo: UndoPayload;
  redo: UndoPayload;
};

type Ctx = {
  canUndo: boolean;
  canRedo: boolean;
  busy: boolean;
  record: (entry: UndoRedoEntry) => void;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  registerRefresh: (fn: () => Promise<void>) => () => void;
  /** Wywołaj odświeżenie zarejestrowane przez aktualny edytor (np. po bulk z modala). */
  refreshEditor: () => Promise<void>;
};

const UndoRedoContext = createContext<Ctx | null>(null);

export function UndoRedoProvider({ children }: { children: ReactNode }) {
  const { showToast } = useToast();
  const refreshRef = useRef<(() => Promise<void>) | null>(null);
  const [undoStack, setUndoStack] = useState<UndoRedoEntry[]>([]);
  const [redoStack, setRedoStack] = useState<UndoRedoEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const busyLockRef = useRef(false);

  const registerRefresh = useCallback((fn: () => Promise<void>) => {
    refreshRef.current = fn;
    return () => {
      if (refreshRef.current === fn) refreshRef.current = null;
    };
  }, []);

  const runRefresh = useCallback(async () => {
    await refreshRef.current?.();
  }, []);

  const record = useCallback((entry: UndoRedoEntry) => {
    if (!entry?.undo || !entry?.redo) return;
    setRedoStack([]);
    setUndoStack((s) => {
      const next = [...s, entry];
      if (next.length > MAX_STACK) next.shift();
      return next;
    });
  }, []);

  const undo = useCallback(async () => {
    if (busyLockRef.current) return;
    let entry: UndoRedoEntry | undefined;
    setUndoStack((s) => {
      if (s.length === 0) return s;
      entry = s[s.length - 1];
      return s.slice(0, -1);
    });
    if (!entry) return;
    busyLockRef.current = true;
    setBusy(true);
    try {
      await applyUndoPayload(entry.undo);
      setRedoStack((s) => [...s, entry!]);
      await runRefresh();
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Undo failed'), 'error');
      setUndoStack((s) => [...s, entry!]);
    } finally {
      busyLockRef.current = false;
      setBusy(false);
    }
  }, [runRefresh, showToast]);

  const redo = useCallback(async () => {
    if (busyLockRef.current) return;
    let entry: UndoRedoEntry | undefined;
    setRedoStack((s) => {
      if (s.length === 0) return s;
      entry = s[s.length - 1];
      return s.slice(0, -1);
    });
    if (!entry) return;
    busyLockRef.current = true;
    setBusy(true);
    try {
      await applyUndoPayload(entry.redo);
      setUndoStack((s) => [...s, entry!]);
      await runRefresh();
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Redo failed'), 'error');
      setRedoStack((s) => [...s, entry!]);
    } finally {
      busyLockRef.current = false;
      setBusy(false);
    }
  }, [runRefresh, showToast]);

  const value = useMemo(
    () => ({
      canUndo: undoStack.length > 0,
      canRedo: redoStack.length > 0,
      busy,
      record,
      undo,
      redo,
      registerRefresh,
      refreshEditor: runRefresh,
    }),
    [undoStack.length, redoStack.length, busy, record, undo, redo, registerRefresh, runRefresh],
  );

  return <UndoRedoContext.Provider value={value}>{children}</UndoRedoContext.Provider>;
}

export function useUndoRedo(): Ctx {
  const ctx = useContext(UndoRedoContext);
  if (!ctx) throw new Error('useUndoRedo requires UndoRedoProvider');
  return ctx;
}
